import { IonCard, IonCardHeader, IonItem, IonLabel, IonList, IonRow, IonText } from "@ionic/react";
import { OverviewCard } from "components/OverviewCard";
import { Page }         from "components/Page";
import { DvcPage }      from "navigation/DvcPage";
import { RouterMap }    from "navigation/RouterMap";
import { useDeviceConnection }    from "functions/Context";
import { Glob, timeGlob, useLoc } from "functions/Language";
import { DeviceInfoPLA, ResponseHead, SupportedDevices, TYPE_PLA } from "functions/networking/NetTypes";
import { useLoader }                                               from "functions/networking/useLoader";
import { FC } from "react";

import { informationCircleOutline } from "ionicons/icons";
import { zfill } from "functions/Formatting";


const route = RouterMap.unitInfo;

const Overview : FC<{
  data: ResponseHead<SupportedDevices>
}> = (props) => {
  const loc = useLoc(glob);

  return <OverviewCard title={loc.title} href={route}>
    <Basic {...props} />
  </OverviewCard>
}

const Basic : FC<{
  data: ResponseHead<SupportedDevices>
}> = (props) => {
  const loc = useLoc(glob);

  return <>
    <IonItem>
      <IonLabel>{loc.desc}</IonLabel>
      <IonText>{props.data.Device.Description}</IonText>
    </IonItem>
    <IonItem>
      <IonLabel>{loc.name}</IonLabel>
      <IonText>{props.data.Device.Name}</IonText>
    </IonItem>
    {props.data.Connection &&
      <IonItem>
        <IonLabel>{loc.connection}</IonLabel>
        <IonText>{props.data.Connection}</IonText>
      </IonItem>
    }
    <IonItem>
      <IonLabel>{loc.fw}</IonLabel>
      <IonText>{props.data.Device.Firmware}</IonText>
    </IonItem>
    {props.data.Device.Version &&
      <IonItem>
        <IonLabel>{loc.hw}</IonLabel>
        <IonText>{props.data.Device.Version}</IonText>
      </IonItem>
    }
  </>
}

const Detail : FC = () => {
  const loc = useLoc(glob);
  const loader = useLoader("Online", null);
  const loaders = [loader];

	const device = useDeviceConnection().device;

	const timeLoc = useLoc(timeGlob);
	let time : string | null = null;
	if (loader.data?.Head.Date_time !== undefined) {
		let raw_time = new Date(loader.data.Head.Date_time);
		time = `${timeLoc.dateLabel(raw_time)}, ${zfill(raw_time.getHours())}:${zfill(raw_time.getMinutes())}:${zfill(raw_time.getSeconds())}`;
	}

  return <Page title={loc.title} loaders={loaders}>
    <div style={{height: "calc(100% - 10px)", marginBottom: "5px", marginTop: "5px"}}>
      <IonRow style={{minHeight: "calc(100%)"}}>
        <IonCard style={{ minWidth: "25em", flex: "1" }}>
          <IonCardHeader>
            {loc.basic}
          </IonCardHeader>
          <IonList>
            {loader.data === null ? "" : <>
              <Basic data={loader.data.Head} />
            </>}
            <IonItem>
              <IonLabel>{loc.mqtt_id}</IonLabel>
              <IonText>{device.Mqtt_uid}</IonText>
            </IonItem>
            {time !== null && <IonItem>
              <IonLabel>{loc.time}</IonLabel>
              <IonText>{time}</IonText>
            </IonItem>}
          </IonList>
        </IonCard>

        {device._type === TYPE_PLA &&
        <IonCard style={{ minWidth: "25em", flex: "1" }}>
          <IonCardHeader>
            {loc.equipment}
          </IonCardHeader>
          <IonList>
            {EquipmentIDs.map((v, i) => <IonItem key={i}>
              <IonLabel>{loc[v]}</IonLabel>
              <IonText>{EquipmentStatus(device.Equipment[v], loc.bStat)}</IonText>
            </IonItem>)}
          </IonList>
        </IonCard>
        }
      </IonRow>
    </div>
  </Page>
}

function EquipmentStatus(val : number | boolean, loc : readonly [string, string]) {
  if (typeof val === "boolean") return loc[val ? 1 : 0];
  else return val;
}

const EquipmentIDs : (keyof DeviceInfoPLA["Equipment"])[] = [
  "Analog_output",
  "Analog_outputs",
  "Analog_inputs_outputs",
  "Data_flash",
  "FRam",
  "Profibus",
  "RJ45",
  "RS485",
  "Relay_outputs",
  "Virtual_flash"
];


const glob = Glob({
	en: {
		title: "Device information",

		basic: "Basic info",
		name: "Type",
		connection: "Connection",
		fw: "Firmware version",
		hw: "Hardware version",
		desc: "Description",
		mqtt_id: "MQTT id",
		time: "Time",

		equipment: "Equipment",
		Analog_inputs_outputs: "# of digital in/out",
		Analog_output: "Has analog out",
		Analog_outputs: "# of digital outs",
		Data_flash: "Data flash",
		FRam: "FRam",
		Profibus: "Profibus",
		RJ45: "RJ45",
		RS485: "RS485",
		Relay_outputs: "Relay ouputs",
		Virtual_flash: "Virtual flash",

		bStat: ["No", "Yes"],
	},
	cs: {
		title: "Informace o přístroji",

		basic: "Základní informace",
		name: "Typ",
		connection: "Zapojení",
		fw: "Verze firmware",
		hw: "Verze hardware",
		desc: "Popis",
		mqtt_id: "MQTT id",
		time: "Čas",

		equipment: "Vybavení",
		Analog_inputs_outputs: "Počet digital in/out",
		Analog_output: "Má analog out",
		Analog_outputs: "Počet digital out",
		Data_flash: "Data flash",
		FRam: "FRam",
		Profibus: "Profibus",
		RJ45: "RJ45",
		RS485: "RS485",
		Relay_outputs: "Počet relé výstupů",
		Virtual_flash: "Virtuální flash",

		bStat: ["Ne", "Ano"],
	},
	pg: {
		title: "Informações do dispositivo",

		basic: "Informações básicas",
		name: "Tipo",
		connection: "Tipo de conexão",
		fw: "Versão do firmware",
		hw: "Versão do hardware",
		desc: "Descrição",
		mqtt_id: "MQTT id",
		time: "Hora",

		equipment: "Equipamentos",
		Analog_inputs_outputs: "N° de in/out digital",
		Analog_output: "Tem saídas analógicas",
		Analog_outputs: "N° de saídas digitais",
		Data_flash: "Data flash",
		FRam: "FRam",
		Profibus: "Profibus",
		RJ45: "RJ45",
		RS485: "RS485",
		Relay_outputs: "Saídas de relés",
		Virtual_flash: "Virtual flash",

		bStat: ["Não", "Sim"],
	},
	es: {
		title: "Información sobre el dispositivo",

		basic: "Información básica",
		name: "Tipo",
		connection: "Tipo de conexión",
		fw: "Versión del firmware",
		hw: "Versión del hardware",
		desc: "Descripción",
		mqtt_id: "MQTT id",
		time: "Hora",

		equipment: "Equipo",
		Analog_inputs_outputs: "N° de in/out digitales",
		Analog_output: "Tiene salidas analógicas",
		Analog_outputs: "N° de salidas digitales",
		Data_flash: "Data flash",
		FRam: "FRam",
		Profibus: "Profibus",
		RJ45: "RJ45",
		RS485: "RS485",
		Relay_outputs: "Salidas de relé",
		Virtual_flash: "Virtual flash",

		bStat: ["No", "Sí"],
	},
} as const);

export const UnitInfo = {
  Overview: Overview,
  path: route,
  Detail: Detail
}

export const PUnitInfo : DvcPage = {
  page: Detail,
  path: route,
  title: glob,
  icon: informationCircleOutline
}
